import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/currency';
import type { NewOrderNotification } from '@/hooks/useRestaurantNewOrderNotifications';

interface RestaurantNewOrderNotificationStackProps {
  notifications: NewOrderNotification[];
  onDismiss: (orderId: string) => void;
  onSelect: (orderId: string) => void;
}

function formatRelativeTime(receivedAt: number): string {
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - receivedAt) / 1000));
  if (elapsedSeconds < 60) return 'Az önce';
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.floor(minutes / 60);
  return `${hours} sa önce`;
}

export function RestaurantNewOrderNotificationStack({
  notifications,
  onDismiss,
  onSelect,
}: RestaurantNewOrderNotificationStackProps) {
  if (notifications.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9990] flex max-h-[min(70vh,520px)] w-full max-w-sm flex-col-reverse gap-3 overflow-y-auto pointer-events-none"
      aria-label="Yeni sipariş bildirimleri"
    >
      {notifications.map((notification) => (
        <div
          key={notification.id}
          role="status"
          aria-live="polite"
          className={cn(
            'relative pointer-events-auto animate-slide-in-right rounded-2xl border border-gray-100 border-l-4 border-l-[#E63946]',
            'bg-white shadow-xl transition-shadow hover:shadow-2xl',
          )}
        >
          <button
            type="button"
            onClick={() => onSelect(notification.id)}
            className="flex w-full items-start gap-3 p-4 pr-10 text-left cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:ring-offset-2"
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-lg"
              aria-hidden
            >
              🔔
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-900 leading-tight">
                Yeni sipariş #{notification.orderNumber}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {notification.itemCount} ürün · {formatCurrency(notification.total)}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                {formatRelativeTime(notification.receivedAt)} · Detay için tıklayın
              </p>
            </div>
          </button>
          <button
            type="button"
            aria-label={`Sipariş ${notification.orderNumber} bildirimini kapat`}
            onClick={(event) => {
              event.stopPropagation();
              onDismiss(notification.id);
            }}
            className="absolute top-3 right-3 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E63946]"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
